import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { KPI, CompanySource, TopicRow, PerformanceRow, ProposedTopicsResponse, ProposedTopicRow } from '@/features/topics/types';

export interface QueryIdmKPIsDto {
  orgId?: number;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface QueryCompetitorsDto {
  orgId?: number;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  sortBy?: 'company' | 'articles' | 'avgScore' | 'avgPosts' | 'share';
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

export interface QueryTopicsDto {
  orgId?: number;
  userId?: string;
  limit?: number;
  contentType?: string;
  type?: 'news' | 'company';
}

export interface QueryPerformanceDto {
  orgId?: number;
  userId?: string;
  industry?: string;
  timeWindow?: string;
  page?: number;
  limit?: number;
}

export interface QueryProposedTopicsDto {
  orgId?: number;
  userId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'category' | 'topicCount' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
  category?: string; // Search by category name
}

export const useIdmKPIs = (
  orgId?: number,
  enabled: boolean = true,
  userId?: string
) => {
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchKPIs = useCallback(
    async (query?: QueryIdmKPIsDto) => {
      if ((!orgId && !userId) || !enabled) return;

      setLoading(true);
      setError(null);
      try {
        const params: QueryIdmKPIsDto = {
          ...(orgId && { orgId }),
          ...(userId && { userId }),
          ...query,
        };
        const { data } = await api.get<KPI[]>('/idm/kpis', {
          params,
        });
        setKpis(data || []);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [orgId, userId, enabled]
  );

  useEffect(() => {
    if (enabled && (orgId || userId)) {
      fetchKPIs();
    }
  }, [orgId, userId, enabled, fetchKPIs]);

  return {
    kpis,
    loading,
    error,
    fetchKPIs,
    refetch: fetchKPIs,
  };
};

export const useIdmCompetitors = (
  orgId?: number,
  enabled: boolean = true,
  userId?: string
) => {
  const [competitors, setCompetitors] = useState<CompanySource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompetitors = useCallback(
    async (query?: QueryCompetitorsDto) => {
      if ((!orgId && !userId) || !enabled) return;

      setLoading(true);
      setError(null);
      try {
        const params: QueryCompetitorsDto = {
          ...(orgId && { orgId }),
          ...(userId && { userId }),
          ...query,
        };
        const { data } = await api.get<CompanySource[]>('/idm/competitors', {
          params,
        });
        setCompetitors(data || []);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [orgId, userId, enabled]
  );

  useEffect(() => {
    if (enabled && (orgId || userId)) {
      fetchCompetitors();
    }
  }, [orgId, userId, enabled, fetchCompetitors]);

  return {
    competitors,
    loading,
    error,
    fetchCompetitors,
    refetch: fetchCompetitors,
  };
};

export const useIdmTopics = (
  orgId?: number,
  enabled: boolean = true,
  userId?: string
) => {
  const [topics, setTopics] = useState<TopicRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTopics = useCallback(
    async (query?: QueryTopicsDto) => {
      if ((!orgId && !userId) || !enabled) return;

      setLoading(true);
      setError(null);
      try {
        const params: QueryTopicsDto = {
          ...(orgId && { orgId }),
          ...(userId && { userId }),
          ...query,
        };
        const { data } = await api.get<TopicRow[]>('/idm/topics', {
          params,
        });
        setTopics(data || []);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [orgId, userId, enabled]
  );

  useEffect(() => {
    if (enabled && (orgId || userId)) {
      fetchTopics();
    }
  }, [orgId, userId, enabled, fetchTopics]);

  const updateTopic = useCallback(
    async (articleId: number, dto: { title?: string; clicks?: number; contentType?: string }) => {
      try {
        const { data } = await api.patch(`/idm/articles/${articleId}`, dto);
        // Refetch topics to get updated data
        await fetchTopics();
        return data;
      } catch (err: unknown) {
        throw new Error(getErrorMessage(err));
      }
    },
    [fetchTopics],
  );

  return {
    topics,
    loading,
    error,
    fetchTopics,
    refetch: fetchTopics,
    updateTopic,
  };
};

export const useIdmPerformance = (
  orgId?: number,
  enabled: boolean = true,
  userId?: string,
  page: number = 1,
  limit: number = 10
) => {
  const [performance, setPerformance] = useState<PerformanceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPerformance = useCallback(
    async (query?: QueryPerformanceDto) => {
      if ((!orgId && !userId) || !enabled) return;

      setLoading(true);
      setError(null);
      try {
        const params: QueryPerformanceDto = {
          ...(orgId && { orgId }),
          ...(userId && { userId }),
          page,
          limit,
          ...query,
        };
        const { data } = await api.get<{
          data: PerformanceRow[];
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        }>('/idm/performance', {
          params,
        });
        setPerformance(data?.data || []);
        setTotal(data?.total || 0);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [orgId, userId, enabled, page, limit]
  );

  useEffect(() => {
    if (enabled && (orgId || userId)) {
      fetchPerformance();
    }
  }, [orgId, userId, enabled, page, limit, fetchPerformance]);

  return {
    performance,
    total,
    loading,
    error,
    fetchPerformance,
    refetch: fetchPerformance,
  };
};

/**
 * Hook to fetch proposed topics from IDM
 * Transforms the API response: combines all topics for each category into a single row
 */
export const useIdmProposedTopics = (
  orgId?: number,
  enabled: boolean = true,
  userId?: string,
  page: number = 1,
  limit: number = 10,
  sortBy: 'category' | 'topicCount' | 'createdAt' = 'createdAt',
  sortOrder: 'ASC' | 'DESC' = 'DESC',
  isInternal: boolean = false
) => {
  const [proposedTopics, setProposedTopics] = useState<ProposedTopicRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(page);
  const [currentLimit, setCurrentLimit] = useState(limit);
  const [currentSortBy, setCurrentSortBy] = useState<'category' | 'topicCount' | 'createdAt'>(sortBy);
  const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>(sortOrder);
  const [totalPages, setTotalPages] = useState(1);

  const fetchProposedTopics = useCallback(
    async (query?: QueryProposedTopicsDto) => {
      if ((!orgId && !userId) || !enabled) return;

      setLoading(true);
      setError(null);
      try {
        const params: QueryProposedTopicsDto = {
          ...(orgId && { orgId }),
          ...(userId && { userId }),
          page: currentPage,
          limit: currentLimit,
          sortBy: currentSortBy,
          sortOrder: currentSortOrder,
          ...query,
        };
        const endpoint = isInternal ? '/idm/internal/proposed-topics' : '/idm/proposed-topics';
        const { data } = await api.get<ProposedTopicsResponse>(endpoint, {
          params,
        });

        // Transform the response: combine all topics for each category into a single row
        const rows: ProposedTopicRow[] = [];
        if (data?.data) {
          data.data.forEach((item) => {
            rows.push({
              category: item.category,
              topics: item.topics || [],
            });
          });
        }

        setProposedTopics(rows);
        setTotal(data?.total || 0);
        setCurrentPage(data?.page || 1);
        setCurrentLimit(data?.limit || 10);
        setTotalPages(data?.totalPages || 1);
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [orgId, userId, enabled, currentPage, currentLimit, currentSortBy, currentSortOrder, isInternal]
  );

  useEffect(() => {
    if (enabled && (orgId || userId)) {
      fetchProposedTopics();
    }
  }, [orgId, userId, enabled, currentPage, currentLimit, currentSortBy, currentSortOrder, fetchProposedTopics]);

  // Update internal state when external page/limit/sorting changes
  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  useEffect(() => {
    setCurrentLimit(limit);
  }, [limit]);

  useEffect(() => {
    setCurrentSortBy(sortBy);
  }, [sortBy]);

  useEffect(() => {
    setCurrentSortOrder(sortOrder);
  }, [sortOrder]);

  return {
    proposedTopics,
    loading,
    error,
    total,
    page: currentPage,
    limit: currentLimit,
    totalPages,
    fetchProposedTopics,
    refetch: fetchProposedTopics,
  };
};

